// =============================================================================
// Form Validation Utilities
// Best practices for reducing user input errors
// =============================================================================

import i18n from '../i18n'

// Shortcut to translate via i18next (works outside React components)
const vt = (key: string, options?: Record<string, unknown>) => i18n.t(`validation.${key}`, options)

// -----------------------------------------------------------------------------
// Name Validation & Formatting
// -----------------------------------------------------------------------------

/**
 * Sanitizes a name by removing invalid characters and formatting properly
 * Allowed: letters (including umlauts), hyphens, apostrophes, spaces
 * Removes: numbers, special characters
 */
export function sanitizeName(name: string): string {
  return name
    // Remove numbers
    .replace(/\d/g, '')
    // Remove special characters except hyphen, apostrophe, space
    .replace(/[^\p{L}\s\-']/gu, '')
    // Replace multiple spaces with single space
    .replace(/\s+/g, ' ')
    // Trim
    .trim()
}

/**
 * Auto-capitalizes a name (first letter of each word)
 * Handles hyphenated names like "Anna-Maria" and "O'Brien"
 */
export function capitalizeName(name: string): string {
  return name
    .toLowerCase()
    .replace(/(^|[\s\-'])\p{L}/gu, (match) => match.toUpperCase())
}

/**
 * Full name formatting: sanitize + capitalize
 */
export function formatName(name: string): string {
  const sanitized = sanitizeName(name)
  return capitalizeName(sanitized)
}

/**
 * Validates a name and returns error message if invalid
 */
export function validateName(name: string, fieldLabel: string): string | undefined {
  const trimmed = name.trim()

  if (!trimmed) return vt('nameRequired', { field: fieldLabel })
  if (trimmed.length < 2) return vt('nameTooShort', { field: fieldLabel })
  if (trimmed.length > 50) return vt('nameTooLong', { field: fieldLabel })
  if (/\d/.test(trimmed)) return vt('nameNoNumbers', { field: fieldLabel })
  if (/[^\p{L}\s'-]/u.test(trimmed)) return vt('nameInvalidChars', { field: fieldLabel })
  if (/['-]{2,}/.test(trimmed)) return vt('nameInvalidSequence', { field: fieldLabel })
  if (/^['-]|['-]$/.test(trimmed)) return vt('nameInvalidStartEnd', { field: fieldLabel })

  return undefined
}

// -----------------------------------------------------------------------------
// City/Place Validation
// -----------------------------------------------------------------------------

/**
 * Sanitizes a city/place name
 * Similar to name but allows dots (for abbreviations like "St.")
 */
export function sanitizeCity(city: string): string {
  return city
    // Remove numbers
    .replace(/\d/g, '')
    // Allow letters, spaces, hyphens, apostrophes, dots
    .replace(/[^\p{L}\s\-'.]/gu, '')
    // Replace multiple spaces with single space
    .replace(/\s+/g, ' ')
    // Trim
    .trim()
}

/**
 * Validates a city/place name
 */
export function validateCity(city: string, fieldLabel: string = 'Stadt'): string | undefined {
  const trimmed = city.trim()

  if (!trimmed) return vt('cityRequired', { field: fieldLabel })
  if (trimmed.length < 2) return vt('cityTooShort', { field: fieldLabel })
  if (trimmed.length > 100) return vt('cityTooLong', { field: fieldLabel })
  if (/\d/.test(trimmed)) return vt('cityNoNumbers', { field: fieldLabel })

  return undefined
}

/**
 * Validates a birth place - only letters, spaces, hyphens, and apostrophes allowed
 */
export function validateBirthPlace(birthPlace: string): string | undefined {
  const trimmed = birthPlace.trim()

  if (!trimmed) return vt('birthPlaceRequired')
  if (trimmed.length < 2) return vt('birthPlaceTooShort')
  if (trimmed.length > 100) return vt('birthPlaceTooLong')
  if (/[^a-zA-ZäöüÄÖÜßàâçéèêëïîôùûüÿœæÀÂÇÉÈÊËÏÎÔÙÛÜŸŒÆ\s\-']/u.test(trimmed)) return vt('birthPlaceLettersOnly')

  return undefined
}

/**
 * Sanitizes birth place input - removes everything except letters, spaces, hyphens, apostrophes
 */
export function sanitizeBirthPlace(value: string): string {
  return value
    // Only keep letters (including umlauts), spaces, hyphens, apostrophes
    .replace(/[^a-zA-ZäöüÄÖÜßàâçéèêëïîôùûüÿœæÀÂÇÉÈÊËÏÎÔÙÛÜŸŒÆ\s\-']/gu, '')
    // Replace multiple spaces with single space
    .replace(/\s+/g, ' ')
}

// -----------------------------------------------------------------------------
// Birth Date Validation
// -----------------------------------------------------------------------------

/**
 * Validates a birth date
 * - Must not be in the future
 * - Must not be more than 120 years ago
 * - Must be at least X years old (for medical consent, typically 18)
 */
export function validateBirthDate(
  dateString: string,
  options: { minAge?: number; maxAge?: number } = {}
): string | undefined {
  const { minAge = 0, maxAge = 120 } = options

  if (!dateString) return vt('birthDateRequired')

  const date = new Date(dateString)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  if (isNaN(date.getTime())) return vt('birthDateInvalid')
  if (date > today) return vt('birthDateFuture')

  // Calendar-correct age (the previous 365.25-day approximation was off by
  // one around birthdays — a patient turning 18 today must pass minAge 18)
  let age = today.getFullYear() - date.getFullYear()
  const birthdayNotYetReached =
    today.getMonth() < date.getMonth() ||
    (today.getMonth() === date.getMonth() && today.getDate() < date.getDate())
  if (birthdayNotYetReached) age--

  if (age > maxAge) return vt('birthDateTooOld', { maxAge })
  if (age < minAge) return vt('birthDateMinAge', { minAge })

  return undefined
}

/**
 * Gets the max date for birth date input (today)
 */
export function getMaxBirthDate(): string {
  return new Date().toISOString().split('T')[0]
}

/**
 * Gets the min date for birth date input (120 years ago)
 */
export function getMinBirthDate(): string {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 120)
  return date.toISOString().split('T')[0]
}

// -----------------------------------------------------------------------------
// Address Validation
// -----------------------------------------------------------------------------

/**
 * Validates a street address
 * Should contain at least some text and ideally a house number
 */
export function validateAddress(address: string): string | undefined {
  const trimmed = address.trim()

  if (!trimmed) return vt('addressRequired')
  if (trimmed.length < 5) return vt('addressTooShort')
  if (trimmed.length > 200) return vt('addressTooLong')
  if (!/\p{L}/u.test(trimmed)) return vt('addressNeedsStreet')

  return undefined
}

// -----------------------------------------------------------------------------
// Postal Code Formatting & Validation
// -----------------------------------------------------------------------------

import { POSTAL_CODE_CONFIGS, type PostalCodeConfig } from '../data/postalCodes'
export { POSTAL_CODE_CONFIGS, type PostalCodeConfig }

/**
 * Formats a postal code based on country rules
 */
export function formatPostalCode(postalCode: string, country: string): string {
  const config = POSTAL_CODE_CONFIGS[country] || POSTAL_CODE_CONFIGS.Other
  if (config.format) {
    return config.format(postalCode)
  }
  return postalCode.trim()
}

/**
 * Validates a postal code for a specific country
 */
export function validatePostalCode(postalCode: string, country: string): string | undefined {
  const trimmed = postalCode.trim()

  if (!trimmed) return vt('postalCodeRequired')

  const config = POSTAL_CODE_CONFIGS[country] || POSTAL_CODE_CONFIGS.Other

  if (!config.pattern.test(trimmed)) {
    return config.example
      ? vt('postalCodeInvalidFormat', { example: config.example })
      : vt('postalCodeInvalid')
  }

  return undefined
}

// -----------------------------------------------------------------------------
// Phone Number Validation (keeping existing logic but adding formatting)
// -----------------------------------------------------------------------------

import { COUNTRY_CODES, type CountryCode } from '../data/countryCodes'
export { COUNTRY_CODES, type CountryCode }

/**
 * Detects the country from a phone number
 */
export function detectPhoneCountry(phone: string): CountryCode | null {
  const cleanPhone = phone.replace(/[\s.()-]/g, '')
  const sorted = [...COUNTRY_CODES].sort((a, b) => b.prefix.length - a.prefix.length)
  for (const country of sorted) {
    if (cleanPhone.startsWith(country.prefix)) {
      return country
    }
  }
  return null
}

/**
 * Formats a phone number (removes letters, keeps digits and +)
 * Also converts 00XX international prefix to +XX (e.g. 0049... → +49...)
 */
export function formatPhoneNumber(phone: string): string {
  let value = phone
    .replace(/[^\d+\s\-.()]/g, '') // keep only digits, +, spaces, and common separators
    .replace(/\s+/g, ' ')          // collapse multiple spaces
    .trim()

  // Convert 00XX international dialing prefix to + (e.g. 0049172... → +49172...)
  // Only at the very start of the string
  if (/^00\d/.test(value.replace(/[\s\-.()]/g, ''))) {
    value = '+' + value.replace(/^[\s\-.()]*00/, '')
  }

  value = stripTrunkPrefix(value)

  return value.replace(/\s+/g, ' ').trim()
}

/**
 * Dial codes whose national numbers carry a trunk prefix '0' that must be
 * dropped when dialling internationally (+49 0172… → +49 172…).
 *
 * Deliberately a allow-list, not a blanket rule: Spain and Portugal have no
 * trunk prefix at all — 60x/61x/68x mobiles contain a significant 0 in second
 * or third position — and Italian landlines KEEP their leading 0 (06 = Rome).
 * Stripping a 0 for those countries silently corrupts the number.
 */
const TRUNK_PREFIX_DIAL_CODES = new Set([
  '+49', // Germany
  '+43', // Austria
  '+41', // Switzerland
  '+33', // France
  '+31', // Netherlands
  '+32', // Belgium
  '+44', // United Kingdom
  '+46', // Sweden
])

/**
 * Drop a single trunk-prefix '0' — anchored to the START of the national part
 * and only for countries that actually use one. Never touches a 0 anywhere
 * else in the number.
 */
function stripTrunkPrefix(value: string): string {
  const country = detectPhoneCountry(value)
  if (!country || !TRUNK_PREFIX_DIAL_CODES.has(country.prefix)) return value
  if (!value.startsWith(country.prefix)) return value

  const rest = value.slice(country.prefix.length)
  // exactly one leading 0, and only when a real digit follows it
  const match = rest.match(/^(\s*)0(\d.*)$/)
  if (!match) return value

  return `${country.prefix} ${match[2]}`
}

/**
 * Normalizes a phone number on blur - converts national formats to international.
 * Intended to run once when the user leaves the phone field.
 * - 0172 1234567  → +49 172 1234567  (German national format)
 * - 0049172...    → +49172...        (already handled by formatPhoneNumber)
 */
export function normalizePhoneNumberOnBlur(phone: string, defaultPrefix = '+49'): string {
  const stripped = phone.replace(/[\s\-.()]/g, '')

  // Already in international format — just clean formatting
  if (stripped.startsWith('+')) {
    return formatPhoneNumber(phone)
  }

  // National format starting with 0 (e.g. 0172 1234567) — prepend the org's
  // dial code. Only strip the leading 0 where that country actually uses a
  // trunk prefix; for Italy (06…) and Spain the 0 is part of the number.
  if (stripped.startsWith('0')) {
    const rest = TRUNK_PREFIX_DIAL_CODES.has(defaultPrefix)
      ? stripped.substring(1)
      : stripped
    return formatPhoneNumber(`${defaultPrefix} ${rest}`)
  }

  return formatPhoneNumber(phone)
}

/**
 * Validates a phone number
 */
// -----------------------------------------------------------------------------
// Yousign Text Field Sanitization
// -----------------------------------------------------------------------------

/**
 * Sanitizes text for Yousign read_only_text fields
 * Preserves common punctuation needed for emails, addresses, and dates
 * Allowed: letters, numbers, spaces, hyphens, forward slashes, dots, commas, @, colons, parentheses
 * Only removes potentially problematic characters: " ' ` # $ % ^ & * [ ] { } < > | \ = ~
 */
/**
 * Validate an email address. Stricter than the previous inline regex:
 * rejects consecutive dots, leading/trailing dots in local part and a
 * TLD shorter than 2 chars.
 */
export function isValidEmail(email: string): boolean {
  const trimmed = email.trim()
  if (trimmed.length < 5 || trimmed.length > 254) return false
  const re = /^[A-Za-z0-9](?:[A-Za-z0-9._%+-]*[A-Za-z0-9_%+-])?@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/
  if (!re.test(trimmed)) return false
  if (trimmed.includes('..')) return false
  return true
}

export function sanitizeForYousign(text: string): string {
  return text
    // Remove only truly problematic special characters, keep common punctuation
    // Preserve + for phone numbers, @ . , : ( ) ? ! ; / - for emails/addresses
    .replace(/["'`#$%^&*[\]{}<>|\\=~]/g, '')
    // Replace multiple spaces with single space
    .replace(/\s+/g, ' ')
    // Trim
    .trim()
}

export function validatePhoneNumber(phone: string): { valid: boolean; error?: string } {
  const cleanPhone = phone.replace(/[\s.()-]/g, '')

  if (!cleanPhone.startsWith('+')) {
    return { valid: false, error: vt('phoneNeedsCountryCode') }
  }

  const country = detectPhoneCountry(cleanPhone)
  if (!country) {
    return { valid: false, error: vt('phoneUnknownCountry') }
  }

  if (cleanPhone.length < country.minLength) {
    return {
      valid: false,
      error: vt('phoneTooShort', {
        country: country.country,
        minDigits: country.minLength - country.prefix.length,
        prefix: country.prefix,
      }),
    }
  }

  if (cleanPhone.length > country.maxLength) {
    return {
      valid: false,
      error: vt('phoneTooLong', {
        country: country.country,
        maxDigits: country.maxLength - country.prefix.length,
        prefix: country.prefix,
      }),
    }
  }

  return { valid: true }
}

// ─── Italian tax code (codice fiscale) ───────────────────────────────────────
// Algorithm supplied by the Italian side (GAM Italy) — treat as the
// authoritative spec; do not "improve" the tables or the regex without a new
// version from them.

/** Character values for ODD 1-based positions (1st, 3rd, 5th, …). */
const TAX_CODE_ODD_VALUES: Readonly<Record<string, number>> = {
  '0': 1,  '1': 0,  '2': 5,  '3': 7,  '4': 9,  '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
  'A': 1,  'B': 0,  'C': 5,  'D': 7,  'E': 9,  'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
  'K': 2,  'L': 4,  'M': 18, 'N': 20, 'O': 11, 'P': 3,  'Q': 6,  'R': 8,  'S': 12, 'T': 14,
  'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23,
}

/** Character values for EVEN 1-based positions (2nd, 4th, 6th, …). */
const TAX_CODE_EVEN_VALUES: Readonly<Record<string, number>> = {
  '0': 0,  '1': 1,  '2': 2,  '3': 3,  '4': 4,  '5': 5,  '6': 6,  '7': 7,  '8': 8,  '9': 9,
  'A': 0,  'B': 1,  'C': 2,  'D': 3,  'E': 4,  'F': 5,  'G': 6,  'H': 7,  'I': 8,  'J': 9,
  'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18, 'T': 19,
  'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25,
}

/** Standard syntax plus the omocodia substitution letters (LMNPQRSTUV). */
const ITALIAN_TAX_CODE_REGEX =
  /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[A-EHLMPR-T][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/

/**
 * Validate an Italian tax code (codice fiscale): syntax + CIN check character.
 * Whitespace-tolerant and case-insensitive. Returns false for an empty value —
 * callers decide whether an empty tax code is acceptable.
 */
export function isValidItalianTaxCode(taxCode: string): boolean {
  if (!taxCode || typeof taxCode !== 'string') return false

  const sanitized = taxCode.trim().toUpperCase()
  if (!ITALIAN_TAX_CODE_REGEX.test(sanitized)) return false

  // CIN: sum the first 15 characters by position, then map mod 26 onto A–Z.
  let total = 0
  for (let i = 0; i < 15; i++) {
    const char = sanitized[i]
    total += (i % 2 === 0) ? TAX_CODE_ODD_VALUES[char] : TAX_CODE_EVEN_VALUES[char]
  }

  return String.fromCharCode(65 + (total % 26)) === sanitized[15]
}

/**
 * True when the street field ends in something that reads as a house number
 * ("Feldstr. 5", "Via Roma 12a").
 *
 * Used to catch the patient who types the number into the street field and then
 * *also* ticks "I have no house number": PVS then receives an unsplit street and
 * an empty house_number, defeating the split entirely (seen with P20310,
 * street "Feldstr. 5", noHouseNumber true). A separator is required before the
 * digits so names like "A7" don't trip it. Advisory only — some street names do
 * legitimately end in a number.
 */
export function streetEndsWithHouseNumber(street: string): boolean {
  return /[\s,]\d+\s*[a-zA-Z]?$/.test((street || '').trim())
}
